import type { TutorTokenBreakdown } from '../types/tutorAi';

/**
 * Paleta categórica do card de composição — cinco slots, todos do `@ads/tokens`.
 *
 * O `@ads/tokens` tem só quatro famílias cromáticas (`accent`, `success`, `warning`,
 * `danger`), e cinco é o teto medido: com estes cinco passos todo par da legenda fica
 * separado por ΔE 10,5 sob protanopia/deuteranopia e 17,0 sob visão normal (OKLab ×100,
 * simulação Machado-Oliveira-Fernandes 2009). Buscando exaustivamente as 17 etapas dos
 * ramps que cabem na banda de luminosidade, seis slots derrubam esse mínimo para 9,2 —
 * abaixo do piso de 15 — e oito para 6,6. Daí a cauda virar {@link OTHERS_COLOR_CLASS}
 * em vez de ganhar um sexto tom: não é preferência, é o limite das famílias disponíveis.
 *
 * Como todos os pares passam (e não apenas os vizinhos), a ordem dos slots é livre — é o
 * que permite atribuir cor por identidade, e não por posição na barra.
 *
 * `success`/`warning` aqui são cor de série, não de estado: este card não tem semântica
 * de status, então verde não significa "bom" nem laranja "atenção".
 */
export const COMPOSITION_COLOR_CLASSES = [
  'bg-base-accent-600',
  'bg-base-accent-300',
  'bg-base-warning-800',
  'bg-base-success-500',
  'bg-base-success-700',
] as const;

/**
 * Cinza da entrada agregada "Outros" — neutro de propósito.
 *
 * Fica fora de {@link COMPOSITION_COLOR_CLASSES} porque não identifica uma série: o croma
 * baixo é o sinal de que aquela fatia é um balde, não um provedor.
 */
export const OTHERS_COLOR_CLASS = 'bg-base-gray-500';

/** Quantas séries recebem cor própria antes de a cauda ser somada em "Outros". */
export const MAX_COLORED_SERIES = COMPOSITION_COLOR_CLASSES.length;

/** Entrada desenhada na barra e na legenda do card de composição. */
export interface TokenCompositionSeries {
  readonly key: string;
  readonly provider: string;
  readonly operation: string;
  /** Classe Tailwind de fundo, de {@link COMPOSITION_COLOR_CLASSES} ou {@link OTHERS_COLOR_CLASS}. */
  readonly colorClass: string;
  readonly tokensUsed: number;
  /** Participação no total, em pontos percentuais (0–100). */
  readonly percent: number;
  /** `true` na entrada agregada da cauda — a única que soma mais de uma série. */
  readonly isOthers: boolean;
  /** Séries somadas nesta entrada; vazio em toda entrada que não seja "Outros". */
  readonly folded: readonly TutorTokenBreakdown[];
}

/** Composição pronta para desenho: o total do período e as entradas já ordenadas. */
export interface TokenComposition {
  readonly total: number;
  readonly series: readonly TokenCompositionSeries[];
}

/**
 * Chave de identidade de uma série.
 *
 * Junta os dois campos com o caractere NUL porque nenhum separador imprimível seria
 * seguro: com um hífen, `"a-b" + "c"` e `"a" + "b-c"` colidiriam na mesma chave e as duas
 * séries receberiam a mesma cor.
 */
function identityOf(item: TutorTokenBreakdown): string {
  return `${item.provider}\u0000${item.operation}`;
}

/**
 * Monta as entradas do card de composição a partir do breakdown cru da API.
 *
 * Faz três coisas que a listagem direta do payload não fazia:
 *
 * 1. **Ordena por consumo decrescente** — a barra empilhada é lida da fatia maior para a
 *    menor, e é o que torna o corte da cauda coerente.
 * 2. **Soma a cauda em "Outros"** — acima de {@link MAX_COLORED_SERIES} não há cor segura
 *    disponível (ver {@link COMPOSITION_COLOR_CLASSES}), e séries de fração de por cento
 *    informam mais somadas do que como fatias invisíveis de cor quase repetida.
 * 3. **Atribui cor por identidade, não por posição** — os slots vão para as séries
 *    coloridas em ordem alfabética de `provider · operation`, de modo que trocar o período
 *    do filtro e reordenar o ranking **não** repinta as séries sobreviventes. A cor só
 *    muda quando a composição do top {@link MAX_COLORED_SERIES} muda de fato.
 *
 * @param breakdown - Breakdown de tokens por provedor e operação, como veio da API
 * @returns Total consumido e as entradas ordenadas, com "Outros" sempre por último
 *
 * @example
 * ```ts
 * const { total, series } = buildTokenComposition(usage.breakdown);
 * series.at(-1)?.isOthers; // true quando houve cauda dobrada
 * ```
 */
export function buildTokenComposition(breakdown: readonly TutorTokenBreakdown[]): TokenComposition {
  const total = breakdown.reduce((sum, item) => sum + item.tokensUsed, 0);
  const shareOf = (tokensUsed: number) => (total === 0 ? 0 : (tokensUsed / total) * 100);

  const byMagnitude = [...breakdown].sort((a, b) => b.tokensUsed - a.tokensUsed);
  const colored = byMagnitude.slice(0, MAX_COLORED_SERIES);
  const folded = byMagnitude.slice(MAX_COLORED_SERIES);

  const slotOf = new Map<string, string>();
  for (const [index, item] of [...colored]
    .sort((a, b) => identityOf(a).localeCompare(identityOf(b)))
    .entries()) {
    slotOf.set(identityOf(item), COMPOSITION_COLOR_CLASSES[index] as string);
  }

  const series: TokenCompositionSeries[] = colored.map((item, index) => ({
    key: `${item.provider}-${item.operation}-${index}`,
    provider: item.provider,
    operation: item.operation,
    colorClass: slotOf.get(identityOf(item)) as string,
    tokensUsed: item.tokensUsed,
    percent: shareOf(item.tokensUsed),
    isOthers: false,
    folded: [],
  }));

  if (folded.length > 0) {
    const tokensUsed = folded.reduce((sum, item) => sum + item.tokensUsed, 0);

    series.push({
      key: 'others',
      provider: '',
      operation: '',
      colorClass: OTHERS_COLOR_CLASS,
      tokensUsed,
      percent: shareOf(tokensUsed),
      isOthers: true,
      folded,
    });
  }

  return { total, series };
}
